// ═══════════════════════════════════════════════
//   NUMBER MERGE — 2048-style sliding tile merger
// ═══════════════════════════════════════════════
//
// Objective:   Slide and merge matching tiles to grow the highest number
//              you can on a NxN board.
// Type:        merge
// Description: Swipe or arrow keys / WASD slide every tile in that
//              direction; equal-value pairs merge once per move. No timer
//              — run ends when no valid moves remain. Reward scales with
//              the round score (gameScore / GAME_MERGE.rewardDivisor).
// Tunables:    GAME_MERGE in data.js.

const {S, canvas, ctx, claimGameReward, GAME_MERGE, addCleanup, showGameOver} = window;

// Layout — recomputed each start in case grid size changes
let MRG_N      = 4;
let MRG_TILE   = 64;
let MRG_GAP    = 4;
let MRG_OX     = 0;
let MRG_OY     = 0;

// 2048-ish palette keyed by exponent (log2 of value)
const MRG_COLORS = {
    2:    {bg: '#fde8c8', fg: '#1a0010'},
    4:    {bg: '#fcd9a3', fg: '#1a0010'},
    8:    {bg: '#fbb069', fg: '#fff5e1'},
    16:   {bg: '#f9905a', fg: '#fff5e1'},
    32:   {bg: '#f76d4a', fg: '#fff5e1'},
    64:   {bg: '#f54c39', fg: '#fff5e1'},
    128:  {bg: '#f5c842', fg: '#1a0010'},
    256:  {bg: '#f5b942', fg: '#1a0010'},
    512:  {bg: '#f5a142', fg: '#1a0010'},
    1024: {bg: '#7dd3fc', fg: '#1a0010'},
    2048: {bg: '#a8e063', fg: '#1a0010'},
    4096: {bg: '#c084fc', fg: '#fff5e1'},
};

// ── Shared window state ──────────────────────────────────────────────
window.mrgBoard   = [];        // 2D array [row][col] of numeric values; 0 = empty
window.mrgGameOver = false;
window.mrgFlash   = 0;         // brief animation hint after a merge

// Pointer-swipe tracking
let mrgPtrStartX = 0;
let mrgPtrStartY = 0;
let mrgPtrTracking = false;

function mrgLayout() {
    const CFG = window.GAME_MERGE;
    MRG_N    = CFG.grid;
    const inner = Math.min(canvas.width, canvas.height) - 36;
    MRG_TILE = Math.floor((inner - (MRG_N - 1) * MRG_GAP) / MRG_N);
    const totalW = MRG_N * MRG_TILE + (MRG_N - 1) * MRG_GAP;
    MRG_OX = Math.floor((canvas.width  - totalW) / 2);
    MRG_OY = Math.floor((canvas.height - totalW) / 2) + 4;
}

function mrgEmptyBoard() {
    const b = [];
    for (let r = 0; r < MRG_N; r++) {
        const row = [];
        for (let c = 0; c < MRG_N; c++) row.push(0);
        b.push(row);
    }
    return b;
}

function mrgEmptyCells() {
    const out = [];
    for (let r = 0; r < MRG_N; r++) {
        for (let c = 0; c < MRG_N; c++) {
            if (window.mrgBoard[r][c] === 0) out.push([r, c]);
        }
    }
    return out;
}

function mrgSpawn() {
    const CFG = window.GAME_MERGE;
    const cells = mrgEmptyCells();
    if (cells.length === 0) return false;
    const [r, c] = cells[Math.floor(Math.random() * cells.length)];
    window.mrgBoard[r][c] = Math.random() < CFG.spawn4Chance ? 4 : 2;
    return true;
}

function mrgHasMoves() {
    for (let r = 0; r < MRG_N; r++) {
        for (let c = 0; c < MRG_N; c++) {
            if (window.mrgBoard[r][c] === 0) return true;
            if (c < MRG_N - 1 && window.mrgBoard[r][c] === window.mrgBoard[r][c + 1]) return true;
            if (r < MRG_N - 1 && window.mrgBoard[r][c] === window.mrgBoard[r + 1][c]) return true;
        }
    }
    return false;
}

// Slide+merge a single row to the left. Returns {row, gained, moved}
function mrgSlideLeft(row) {
    const compact = row.filter(v => v !== 0);
    let gained = 0;
    const out = [];
    for (let i = 0; i < compact.length; i++) {
        if (i + 1 < compact.length && compact[i] === compact[i + 1]) {
            const merged = compact[i] * 2;
            out.push(merged);
            gained += merged;
            i++; // skip the merged neighbour
        } else {
            out.push(compact[i]);
        }
    }
    while (out.length < MRG_N) out.push(0);
    const moved = out.some((v, i) => v !== row[i]);
    return {row: out, gained, moved};
}

function mrgMove(dir) {
    if (!window.gameRunning || window.mrgGameOver) return;

    const CFG = window.GAME_MERGE;
    let totalGain = 0;
    let anyMoved = false;
    const N = MRG_N;

    // Reduce all 4 directions to "slide left" by extracting/rotating rows
    if (dir === 'left' || dir === 'right') {
        for (let r = 0; r < N; r++) {
            let row = window.mrgBoard[r].slice();
            if (dir === 'right') row.reverse();
            const res = mrgSlideLeft(row);
            if (dir === 'right') res.row.reverse();
            window.mrgBoard[r] = res.row;
            totalGain += res.gained;
            if (res.moved) anyMoved = true;
        }
    } else { // up / down
        for (let c = 0; c < N; c++) {
            let col = [];
            for (let r = 0; r < N; r++) col.push(window.mrgBoard[r][c]);
            if (dir === 'down') col.reverse();
            const res = mrgSlideLeft(col);
            if (dir === 'down') res.row.reverse();
            for (let r = 0; r < N; r++) window.mrgBoard[r][c] = res.row[r];
            totalGain += res.gained;
            if (res.moved) anyMoved = true;
        }
    }

    if (!anyMoved) {
        SFX.play('error');
        return;
    }

    if (totalGain > 0) {
        window.gameScore += totalGain;
        document.getElementById('mg-score').textContent = window.gameScore;
        SFX.play('hit');
        window.mrgFlash = 8;
    } else {
        SFX.play('click');
    }

    // Spawn new tile(s)
    for (let i = 0; i < CFG.spawnPerMove; i++) mrgSpawn();

    mrgDraw();

    if (!mrgHasMoves()) {
        window.mrgGameOver = true;
        endMerge();
    }
}

// ── Game lifecycle ────────────────────────────────────────────────────

function startMerge() {
    const CFG = window.GAME_MERGE;
    mrgLayout();
    window.mrgBoard = mrgEmptyBoard();
    window.mrgGameOver = false;
    window.mrgFlash = 0;
    window.gameRunning = true;
    window.gameScore = 0;

    document.getElementById('mg-score').textContent = '0';
    document.getElementById('mg-timer').textContent = ''; // no timer for this game

    for (let i = 0; i < CFG.startTiles; i++) mrgSpawn();

    canvas.addEventListener('pointerdown', mrgPtrDown);
    canvas.addEventListener('pointerup',   mrgPtrUp);
    canvas.addEventListener('pointercancel', mrgPtrCancel);
    document.addEventListener('keydown',   mrgKey);
    addCleanup(() => canvas.removeEventListener('pointerdown', mrgPtrDown));
    addCleanup(() => canvas.removeEventListener('pointerup',   mrgPtrUp));
    addCleanup(() => canvas.removeEventListener('pointercancel', mrgPtrCancel));
    addCleanup(() => document.removeEventListener('keydown',   mrgKey));
    addCleanup(() => {
        window.mrgBoard = [];
        window.mrgGameOver = false;
        window.mrgFlash = 0;
    });

    mrgDraw();
}

function mrgPtrDown(e) {
    if (!window.gameRunning || S.currentGame !== 'merge') return;
    mrgPtrTracking = true;
    mrgPtrStartX = e.clientX;
    mrgPtrStartY = e.clientY;
}

function mrgPtrUp(e) {
    if (!mrgPtrTracking) return;
    mrgPtrTracking = false;
    if (!window.gameRunning || S.currentGame !== 'merge') return;
    const CFG = window.GAME_MERGE;
    const dx = e.clientX - mrgPtrStartX;
    const dy = e.clientY - mrgPtrStartY;
    const adx = Math.abs(dx), ady = Math.abs(dy);
    if (Math.max(adx, ady) < CFG.swipeThreshold) return;
    if (adx > ady) mrgMove(dx > 0 ? 'right' : 'left');
    else           mrgMove(dy > 0 ? 'down'  : 'up');
}

function mrgPtrCancel() {
    mrgPtrTracking = false;
}

function mrgKey(e) {
    if (!window.gameRunning || S.currentGame !== 'merge') return;
    let dir = null;
    if (e.code === 'ArrowLeft'  || e.code === 'KeyA') dir = 'left';
    else if (e.code === 'ArrowRight' || e.code === 'KeyD') dir = 'right';
    else if (e.code === 'ArrowUp'    || e.code === 'KeyW') dir = 'up';
    else if (e.code === 'ArrowDown'  || e.code === 'KeyS') dir = 'down';
    if (!dir) return;
    e.preventDefault();
    mrgMove(dir);
}

function endMerge() {
    window.gameRunning = false;
    const CFG = window.GAME_MERGE;
    SFX.play('gameover');
    const p = Math.min(CFG.rewardMax, Math.floor(window.gameScore / CFG.rewardDivisor));
    let highest = 0;
    for (let r = 0; r < MRG_N; r++) {
        for (let c = 0; c < MRG_N; c++) {
            if (window.mrgBoard[r][c] > highest) highest = window.mrgBoard[r][c];
        }
    }
    showGameOver(`Best tile: ${highest} · Score: ${window.gameScore}\n` + claimGameReward('merge', window.gameScore, p));
}

// ── Render ───────────────────────────────────────────────────────────

function mrgDraw() {
    const W = canvas.width, H = canvas.height;
    ctx.fillStyle = '#1a0010';
    ctx.fillRect(0, 0, W, H);

    // Board frame
    const totalW = MRG_N * MRG_TILE + (MRG_N - 1) * MRG_GAP;
    const padX = 6, padY = 6;
    ctx.fillStyle = 'rgba(247,37,133,0.10)';
    ctx.beginPath();
    ctx.roundRect(MRG_OX - padX, MRG_OY - padY, totalW + padX * 2, totalW + padY * 2, 10);
    ctx.fill();

    // Empty cells background grid
    for (let r = 0; r < MRG_N; r++) {
        for (let c = 0; c < MRG_N; c++) {
            const x = MRG_OX + c * (MRG_TILE + MRG_GAP);
            const y = MRG_OY + r * (MRG_TILE + MRG_GAP);
            ctx.fillStyle = 'rgba(255,255,255,0.05)';
            ctx.beginPath();
            ctx.roundRect(x, y, MRG_TILE, MRG_TILE, 6);
            ctx.fill();
        }
    }

    // Tiles
    for (let r = 0; r < MRG_N; r++) {
        for (let c = 0; c < MRG_N; c++) {
            const v = window.mrgBoard[r][c];
            if (v === 0) continue;
            const x = MRG_OX + c * (MRG_TILE + MRG_GAP);
            const y = MRG_OY + r * (MRG_TILE + MRG_GAP);
            const colors = MRG_COLORS[v] || {bg: '#c084fc', fg: '#fff5e1'};

            // shadow
            ctx.fillStyle = 'rgba(0,0,0,0.4)';
            ctx.beginPath();
            ctx.roundRect(x + 2, y + 2, MRG_TILE, MRG_TILE, 6);
            ctx.fill();

            ctx.fillStyle = colors.bg;
            ctx.beginPath();
            ctx.roundRect(x, y, MRG_TILE, MRG_TILE, 6);
            ctx.fill();
            ctx.strokeStyle = '#1a0010';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Number — font shrinks for longer values
            const digits = String(v).length;
            const fontSize = digits <= 2 ? 18 : digits <= 3 ? 14 : 11;
            ctx.fillStyle = colors.fg;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = `bold ${fontSize}px "Press Start 2P", monospace`;
            ctx.fillText(String(v), x + MRG_TILE / 2, y + MRG_TILE / 2 + 1);
        }
    }

    if (window.mrgFlash > 0) window.mrgFlash--;

    // Footer hint
    ctx.font      = '6px "Press Start 2P", monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(247,37,133,0.55)';
    ctx.fillText('SWIPE  /  ARROW KEYS  to merge!', W / 2, H - 6);
}

// ── Expose ───────────────────────────────────────────────────────────
window.startMerge   = startMerge;
window.endMerge     = endMerge;
window.mrgPtrDown   = mrgPtrDown;
window.mrgPtrUp     = mrgPtrUp;
window.mrgPtrCancel = mrgPtrCancel;
window.mrgKey       = mrgKey;
window.mrgDraw      = mrgDraw;
